import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { MasterDataService } from '@shared/data-provider/master-data-api/api/masterData.service';
import { Country } from '@shared/data-provider/master-data-api/model/country';
import { convertDateToUTC } from '@shared/functions/dateFunctions';
import { NzModalService } from 'ng-zorro-antd/modal';
import { map } from 'rxjs';
import { MasterDataFunctionsService } from '../../services/master-data.service';
import { CustomFormValidators } from '@shared/custom-form-validators';
import { Region } from '@shared/data-provider/master-data-api/model/region';
import { SettingsService } from '@core/services/settings/settings.service';

interface ExtendedCountry extends Country {
	nameEn?: string;
	nameRu?: string;
	nameLocal?: string;
	regionLocal?: string;
	dtRangeStart?: Date;
	dtRangeFinish?: Date;
}

@Component({
	selector: 'app-countries',
	templateUrl: './countries.component.html',
	styleUrls: ['./countries.component.less'],
})
export class CountriesComponent implements OnInit {
	constructor(
		private apiMasterData: MasterDataService,
		private settingsService: SettingsService,
		private modal: NzModalService,
		private fb: UntypedFormBuilder,
		private globalI18n: GlobalI18n,
		private functionsService: MasterDataFunctionsService
	) {}

	private formValidators: CustomFormValidators = new CustomFormValidators();

	private _list: ExtendedCountry[] = [];
	selectedItem: ExtendedCountry;
	form: FormGroup;

	listOfDisplayRegions: { value: number; label: string }[] = [];

	settingValue = {
		loadingList: false,
		loadingModal: false,
		saving: false,
		searchIata: '',
		searchIcao: '',
		searchName: '',
		isVisibleModal: false,
		loadedPercent: null,
		pageIndex: 1,
	};

	listOfColumn = [];

	ngOnInit() {
		this.loadCountries();
		this.loadList();
		this.listOfColumn = [
			{
				title: 'IATA',
				compare: (a: ExtendedCountry, b: ExtendedCountry) => a.iata.localeCompare(b.iata),
				priority: false,
			},
			{
				title: 'ICAO',
				compare: (a: ExtendedCountry, b: ExtendedCountry) => a.icao.localeCompare(b.icao),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnRegion'),
				compare: (a: ExtendedCountry, b: ExtendedCountry) => a.regionLocal.localeCompare(b.regionLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnName'),
				compare: (a: ExtendedCountry, b: ExtendedCountry) => a.nameLocal.localeCompare(b.nameLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnLastUpdate'),
				compare: (a: ExtendedCountry, b: ExtendedCountry) => Date.parse(a.lastupdate) - Date.parse(b.lastupdate),
				priority: false,
			},
		];
	}

	loadCountries() {
		this.apiMasterData.selectRecords('regions').subscribe((data: Region[]) => {
			this.listOfDisplayRegions = data.map(item => ({
				value: item.id,
				label: item.name + ' (' + item.iata + '/' + item.icao + ')',
			}));
		});
	}

	loadList() {
		this.settingValue.loadingList = true;
		this.apiMasterData.selectRecords('countries').subscribe((data: Country[]) => {
			this._list = data.map(item => {
				return {
					...item,
					nameEn: item.name[0],
					nameRu: item.name[1],
					nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
					regionLocal: this.settingsService.language == 'EN' ? item.region[0] : item.region[1],
					dtRangeStart: new Date(item.dtRange[0]),
					dtRangeFinish: new Date(item.dtRange[1]),
				};
			});
			this.settingValue.loadingList = false;
		});
	}

	get listOfDisplay(): ExtendedCountry[] {
		// При установленных фильтрах необходимо сбросить пагинацию на 1 страницу
		if (
			this.settingValue.searchIata.length !== 0 ||
			this.settingValue.searchIcao.length !== 0 ||
			this.settingValue.searchName.length !== 0
		) {
			this.onPageIndexChanged(1);
		}
		return this._list.filter(item => {
			return (
				item.iata.toLowerCase().indexOf(this.settingValue.searchIata.toLowerCase()) != -1 &&
				item.icao.toLowerCase().indexOf(this.settingValue.searchIcao.toLowerCase()) != -1 &&
				(item.nameRu.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1 ||
					item.nameEn.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1)
			);
		});
	}

	openItem(id: number) {
		this.settingValue.loadingList = true;
		return this.apiMasterData
			.selectRecord('countries', id.toString())
			.pipe(
				map((item: Country) => {
					return {
						...item,
						nameEn: item.name[0],
						nameRu: item.name[1],
						nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
						dtRangeStart: new Date(item.dtRange[0]),
						dtRangeFinish: new Date(item.dtRange[1]),
					};
				})
			)
			.subscribe((data: ExtendedCountry) => {
				this.selectedItem = data;
				this.form = this.createForm(data);
				this.showModal();
				this.settingValue.loadingList = false;
			});
	}

	addNew() {
		this.selectedItem = {};
		this.form = this.createForm();
		this.showModal();
	}

	createForm(data?: ExtendedCountry) {
		return this.fb.group({
			iata: [
				data ? data.iata : '',
				[Validators.required, this.formValidators.onlyEnglishAndNumbers(), this.formValidators.lengthValidator(2)],
			],
			icao: [
				data ? data.icao : '',
				[Validators.required, this.formValidators.onlyEnglishAndNumbers(), this.formValidators.lengthValidator(3)],
			],
			nameEn: [data ? data.nameEn : '', [Validators.required, this.formValidators.onlyEnglish()]],
			nameRu: [data ? data.nameRu : '', [Validators.required]],
			regionId: [data ? data.regionId : '', [Validators.required]],
			dtRangeStart: [data ? convertDateToUTC(new Date(data.dtRangeStart)) : new Date(), [Validators.required]],
			dtRangeFinish: [
				data ? convertDateToUTC(new Date(data.dtRangeFinish)) : new Date('2099-12-31 23:59'),
				[Validators.required],
			],
		});
	}

	add() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.insertRecord('countries', null, this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => {
					this.displayError(err);
				},
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	save() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.updateRecord('countries', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => this.displayError(err),
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	delete() {
		this.settingValue.saving = true;
		this.form.get('dtRangeFinish').setValue(new Date());
		this.apiMasterData.updateRecord('countries', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
			next: () => {
				this.closeModal();
				this.loadList();
			},
			error: err => this.displayError(err),
			complete: () => {
				this.settingValue.saving = false;
			},
		});
	}

	duplicate() {
		this.selectedItem.id = undefined;
		this.selectedItem.lastupdate = undefined;
	}

	showDeleteConfirm(): void {
		this.modal.confirm({
			nzTitle: this.globalI18n.getMessage(Module.MasterData, 'deleteItem'),
			nzOkText: this.globalI18n.getMessage(Module.MasterData, 'yes'),
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.delete(),
			nzCancelText: this.globalI18n.getMessage(Module.MasterData, 'no'),
			nzOnCancel: () => console.log('Cancel'),
		});
	}

	createFromForm(): Country {
		return {
			...this.selectedItem,
			...this.form.value,
			name: [this.form.get('nameEn').value, this.form.get('nameRu').value],
			dtRange: [this.form.get('dtRangeStart').value, this.form.get('dtRangeFinish').value],
		};
	}

	downloadCsvFile() {
		this.functionsService.downloadCsvFile('countries', this.settingValue.loadingList);
	}

	downloadXmlFile() {
		this.functionsService.downloadXmlFile('countries', this.settingValue.loadingList);
	}

	onPageIndexChanged(index: number) {
		this.settingValue.pageIndex = index;
	}

	showModal(): void {
		this.settingValue.isVisibleModal = true;
	}

	closeModal(): void {
		this.settingValue.isVisibleModal = false;
	}

	displayError(err): void {
		this.modal.create({
			nzTitle: 'Error',
			nzContent: err.message,
			nzClosable: false,
		});
	}
}
